import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiUtils } from '../services/api';
import {
  LoginContainer,
  BackgroundShapes,
  LoginCard,
  Logo,
  Form,
  FormGroup,
  Label,
  InputContainer,
  Input,
  InputIcon,
  PasswordToggleButton,
  LoginButton,
  LoadingSpinner,
  ErrorMessage,
  ForgotPassword,
  FeatureHighlight
} from '../styles/LoginPage.styles';

function AdminLoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated, userType, login } = useAuth();

  useEffect(() => {
    // Check if user is already authenticated
    if (isAuthenticated && userType) {
      // Redirect to appropriate portal based on user type
      navigate(userType === 'admin' ? '/admin' : '/presenter');
    }
  }, [isAuthenticated, userType, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Use the login function from Auth context with admin type
      await login(formData, 'admin');
      
      // Navigation will be handled by the useEffect above
      // when the authentication state changes
    } catch (error) {
      setError(apiUtils.formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <BackgroundShapes>
        <div className="shape"></div>
        <div className="shape"></div>
        <div className="shape"></div>
        <div className="shape"></div>
      </BackgroundShapes>
      
      <LoginCard>
        <Logo>
          <span className="logo-icon">⚡</span>
          <div className="logo-text">BidMaster Pro</div>
          <div className="logo-subtitle">Admin Portal</div>
        </Logo>

        {error && (
          <ErrorMessage>
            <span className="error-icon">⚠️</span>
            {error}
          </ErrorMessage>
        )}

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Email Address</Label>
            <InputContainer>
              <InputIcon>📧</InputIcon>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                required
                disabled={loading}
              />
            </InputContainer>
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <InputContainer>
              <InputIcon>🔒</InputIcon>
              <Input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <PasswordToggleButton
                type="button"
                onClick={togglePasswordVisibility}
                disabled={loading}
              >
                {showPassword ? '🙈' : '👁️'}
              </PasswordToggleButton>
            </InputContainer>
          </FormGroup>

          <LoginButton type="submit" disabled={loading}>
            {loading ? (
              <>
                <LoadingSpinner />
                Signing In...
              </>
            ) : (
              <>
                🚀 Sign In to Admin Portal
              </>
            )}
          </LoginButton>
        </Form>

        <ForgotPassword href="#" onClick={(e) => e.preventDefault()}>
          Forgot your password?
        </ForgotPassword>

        <FeatureHighlight>
          <span className="highlight-icon">⚙️</span>
          <div className="highlight-text">
            Manage items, radio shows, and presenters with full administrative control
          </div>
        </FeatureHighlight>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '2rem', 
          padding: '1rem', 
          borderTop: '1px solid #e2e8f0',
          fontSize: '0.9rem',
          color: '#64748b'
        }}>
          <p>Are you a presenter? <a 
            href="/presenter-login" 
            style={{ color: '#2563eb', textDecoration: 'underline' }}
          >
            Sign in here
          </a></p>
        </div>
      </LoginCard>
    </LoginContainer>
  );
}

export default AdminLoginPage; 